import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';

const defaultContent = {
  hero: {
    title: 'Contact Us',
    subtitle: "Get in Touch | We're Here to Help | Connect with FIT",
    backgroundImage: 'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
  },
  address: {
    name: 'FIT Group of Institutions',
    line1: 'NH-58, Partapur Bypass',
    line2: 'Near Partapur',
    city: 'Meerut',
    state: 'Uttar Pradesh',
    pincode: '250103',
    country: 'India'
  },
  phones: {
    main: '+91 121-2578888',
    admission: '+91 121-2578889',
    academic: '+91 121-2578890',
    placement: '+91 121-2578891'
  },
  emails: {
    general: 'info@fitenggmeerut.com',
    admission: 'admissions@fitenggmeerut.com',
    academic: 'academics@fitenggmeerut.com',
    placement: 'placements@fitenggmeerut.com'
  },
  officeHours: {
    weekdays: '9:00 AM - 5:00 PM',
    saturday: '9:00 AM - 2:00 PM',
    sunday: 'Closed',
    note: 'Emergency contact available 24/7 for current students through student portal.'
  },
  socialLinks: {
    facebook: 'https://www.facebook.com/fitenggmeerut',
    twitter: '',
    linkedin: '',
    instagram: '',
    youtube: ''
  },
  mapEmbedUrl: ''
};

const ContactPageEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [content, setContent] = useState(defaultContent);
  const [seoMeta, setSeoMeta] = useState({ metaTitle: '', metaDescription: '', keywords: [] });
  const [isPublished, setIsPublished] = useState(true);
  const [pageExists, setPageExists] = useState(false);

  useEffect(() => {
    fetchPage();
  }, []);

  const fetchPage = async () => {
    try {
      const response = await apiService.getPage('contact');
      if (response.success && response.data.page) {
        const page = response.data.page;
        setContent({ ...defaultContent, ...page.content });
        setSeoMeta(page.seoMeta || { metaTitle: '', metaDescription: '', keywords: [] });
        setIsPublished(page.isPublished);
        setPageExists(true);
      }
    } catch (err) {
      // Page doesn't exist yet, use defaults
      setPageExists(false);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const pageData = {
        pageId: 'contact',
        title: 'Contact Us',
        content,
        seoMeta,
        isPublished
      };

      if (pageExists) {
        await apiService.updatePage('contact', pageData);
      } else {
        await apiService.createPage(pageData);
        setPageExists(true);
      }
      setSuccess('Contact page updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to save contact page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading contact page...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-slate">Edit Contact Page</h1>
        <button
          onClick={() => navigate('/admin')}
          className="px-4 py-2 bg-gray-100 text-slate rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
        >
          <i className="fas fa-arrow-left"></i>
          Back
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>{success}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">

        {/* Hero / Banner */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">Banner Section</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Banner Title</label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.hero?.title || ''}
                onChange={(e) => updateField('hero', 'title', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Banner Subtitle</label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.hero?.subtitle || ''}
                onChange={(e) => updateField('hero', 'subtitle', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate mb-1">Banner Background Image</label>
              <ImageUpload
                value={content.hero?.backgroundImage || ''}
                onChange={(value) => updateField('hero', 'backgroundImage', value)}
                placeholder="Upload or paste banner image URL"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-map-marker-alt text-crimson mr-2"></i>Office Address
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate mb-1">Institute Name</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.name || ''} onChange={(e) => updateField('address', 'name', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Address Line 1</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.line1 || ''} onChange={(e) => updateField('address', 'line1', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Address Line 2</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.line2 || ''} onChange={(e) => updateField('address', 'line2', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">City</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.city || ''} onChange={(e) => updateField('address', 'city', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">State</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.state || ''} onChange={(e) => updateField('address', 'state', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Pincode</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.pincode || ''} onChange={(e) => updateField('address', 'pincode', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Country</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.address?.country || ''} onChange={(e) => updateField('address', 'country', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Phone Numbers */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-phone text-gold mr-2"></i>Phone Numbers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Main</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.phones?.main || ''} onChange={(e) => updateField('phones', 'main', e.target.value)} placeholder="+91 98765-43210" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Admission</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.phones?.admission || ''} onChange={(e) => updateField('phones', 'admission', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Academic</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.phones?.academic || ''} onChange={(e) => updateField('phones', 'academic', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Placement</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.phones?.placement || ''} onChange={(e) => updateField('phones', 'placement', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Email Addresses */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-envelope text-slate mr-2"></i>Email Addresses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">General</label>
              <input type="email" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.emails?.general || ''} onChange={(e) => updateField('emails', 'general', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Admission</label>
              <input type="email" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.emails?.admission || ''} onChange={(e) => updateField('emails', 'admission', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Academic</label>
              <input type="email" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.emails?.academic || ''} onChange={(e) => updateField('emails', 'academic', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Placement</label>
              <input type="email" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.emails?.placement || ''} onChange={(e) => updateField('emails', 'placement', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Office Hours */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-clock text-blue-600 mr-2"></i>Office Hours
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Monday - Friday</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.officeHours?.weekdays || ''} onChange={(e) => updateField('officeHours', 'weekdays', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Saturday</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.officeHours?.saturday || ''} onChange={(e) => updateField('officeHours', 'saturday', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Sunday</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.officeHours?.sunday || ''} onChange={(e) => updateField('officeHours', 'sunday', e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-slate mb-1">Note</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.officeHours?.note || ''} onChange={(e) => updateField('officeHours', 'note', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-share-alt text-pink-500 mr-2"></i>Social Media Links
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1"><i className="fab fa-facebook text-blue-600 mr-1"></i>Facebook</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.socialLinks?.facebook || ''} onChange={(e) => updateField('socialLinks', 'facebook', e.target.value)} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1"><i className="fab fa-twitter text-sky-500 mr-1"></i>Twitter</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.socialLinks?.twitter || ''} onChange={(e) => updateField('socialLinks', 'twitter', e.target.value)} placeholder="https://twitter.com/..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1"><i className="fab fa-linkedin text-blue-700 mr-1"></i>LinkedIn</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.socialLinks?.linkedin || ''} onChange={(e) => updateField('socialLinks', 'linkedin', e.target.value)} placeholder="https://linkedin.com/..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1"><i className="fab fa-instagram text-pink-500 mr-1"></i>Instagram</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.socialLinks?.instagram || ''} onChange={(e) => updateField('socialLinks', 'instagram', e.target.value)} placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1"><i className="fab fa-youtube text-red-600 mr-1"></i>YouTube</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={content.socialLinks?.youtube || ''} onChange={(e) => updateField('socialLinks', 'youtube', e.target.value)} placeholder="https://youtube.com/..." />
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-map text-green-600 mr-2"></i>Google Map
          </h2>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Google Maps Embed URL</label>
            <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              value={content.mapEmbedUrl || ''}
              onChange={(e) => setContent(prev => ({ ...prev, mapEmbedUrl: e.target.value }))}
              placeholder="https://www.google.com/maps/embed?pb=..." />
            <p className="text-xs text-gray-500 mt-1">Go to Google Maps, search your location, click Share, choose Embed, and paste the src URL here.</p>
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-semibold text-slate mb-4">
            <i className="fas fa-search text-gray-500 mr-2"></i>SEO Settings
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Meta Title</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={seoMeta.metaTitle || ''} onChange={(e) => setSeoMeta(prev => ({ ...prev, metaTitle: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Meta Description</label>
              <textarea className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson" rows={2}
                value={seoMeta.metaDescription || ''} onChange={(e) => setSeoMeta(prev => ({ ...prev, metaDescription: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate mb-1">Keywords (comma-separated)</label>
              <input type="text" className="w-full px-4 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                value={seoMeta.keywords?.join(', ') || ''} onChange={(e) => setSeoMeta(prev => ({ ...prev, keywords: e.target.value.split(',').map(k => k.trim()) }))} />
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate('/admin')}
            className="px-6 py-3 bg-gray-100 text-slate rounded-lg font-medium hover:bg-gray-200">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 flex items-center gap-2">
            {saving ? <><i className="fas fa-spinner fa-spin"></i>Saving...</> : <><i className="fas fa-save"></i>Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactPageEdit;
